import React from 'react';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Compass,
  Users,
} from 'lucide-react';
import { SiteFooter } from './SiteFooter';

const WHAT_WE_DO = [
  { title: 'The Marketing Tarot Reading', subtitle: 'A strategic diagnostic experience' },
  { title: 'Brand Alchemy', subtitle: 'Turning Ideas into Powerful Brands' },
  { title: 'Storytelling Alchemy', subtitle: 'Making Brands Memorable' },
  { title: 'Community & Influence Alchemy', subtitle: 'Partnership strategy that builds trust and reach.' },
];

const WHO_WE_SERVE = [
  'Founders',
  'Artistic organizations',
  'Creative teams',
  'Community-driven brands',
];

export const AboutPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a1628] via-[#0f1f35] to-[#0a1628] text-[#e8dcc8]">
      {/* Top bar */}
      <div className="max-w-6xl mx-auto px-6 pt-6 flex items-center justify-between gap-3">
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-2 text-[#d4c4a8]/60 hover:text-[#e89b47] transition-colors font-body text-sm"
          data-testid="about-back-btn"
        >
          <ArrowLeft className="w-4 h-4 hidden" />
          Back to Home
        </button>
        <a
          href="https://calendly.com/marketingtarot/30min"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 h-10 px-5 text-sm bg-gradient-to-r from-[#e89b47] to-[#d4883a] text-[#0a1628] hover:from-[#e89b47]/90 hover:to-[#d4883a]/90 font-display tracking-widest shadow-lg shadow-[#e89b47]/25 rounded-xl transition-all hover:scale-105 hover:shadow-xl hover:shadow-[#e89b47]/30"
          data-testid="about-book-call-btn"
        >
          Book a Clarity Call
        </a>
      </div>

      {/* Hero */}
      <section className="max-w-4xl mx-auto px-6 pt-16 pb-16 text-center">
        <p className="font-display text-sm tracking-[0.3em] text-[#e89b47] mb-6 uppercase">
          About Us
        </p>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#e8dcc8] leading-tight mb-8" data-testid="about-headline">
          Marketing is a reading of <span className="text-[#e89b47]">human behaviour</span>.
        </h1>
        <div className="max-w-2xl mx-auto space-y-5 text-left sm:text-center">
          <p className="font-body text-lg text-[#d4c4a8]/80 leading-relaxed">
            The Marketing Alchemist sees patterns where others see chaos. We translate intuition into strategy, turning creative energy into clarity.
          </p>
          <p className="font-body text-lg text-[#d4c4a8]/80 leading-relaxed">
            The Marketing Tarot was created for founders, creators, and artistic teams who want clarity — not noise.
          </p>
          <p className="font-body text-lg text-[#d4c4a8]/80 leading-relaxed">
            We are a consulting firm with a twist: we lead with intuition and strategy.
          </p>
          <p className="font-body text-lg text-[#d4c4a8]/80 leading-relaxed">
            Developed by a Global Marketing Strategist with two decades of experience across startups, industries, products, and audiences, we believe clarity comes from pattern, story, and archetype.
          </p>
          <p className="font-body text-lg text-[#d4c4a8]/80 leading-relaxed">
            The Marketing Tarot blends brand psychology, creative intuition, and practical marketing wisdom — one card at a time.
          </p>
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-6xl mx-auto px-6">
        <div className="h-px bg-gradient-to-r from-transparent via-[#e89b47]/30 to-transparent" />
      </div>

      {/* What We Do */}
      <section className="max-w-4xl mx-auto px-6 py-20">
        <div className="flex items-center justify-center gap-3 mb-8">
          <Compass className="w-6 h-6 text-[#e89b47]" />
          <h2 className="font-display text-2xl sm:text-3xl text-[#e8dcc8]" data-testid="what-we-do-heading">
            What We Do
          </h2>
        </div>

        <div className="max-w-2xl mx-auto space-y-5 text-center">
          <p className="font-body text-lg text-[#d4c4a8]/80 leading-relaxed">
            We help builders, creators, and leaders understand who they are, who they serve, and how to communicate with resonance.
          </p>
          <p className="font-display text-sm text-[#e89b47] tracking-wider uppercase pt-2">Through:</p>
          <div className="space-y-4">
            {WHAT_WE_DO.map((item, i) => (
              <p key={i} className="font-body text-lg leading-relaxed">
                <span className="text-[#e8dcc8]">{item.title}</span>
                <span className="italic text-[#d4c4a8]/60"> — {item.subtitle}</span>
              </p>
            ))}
          </div>
        </div>
        <p className="font-body text-[#d4c4a8]/60 text-center mt-8 italic max-w-2xl mx-auto">
          Our work sits at the intersection of creativity, empathy, and clarity — where strategy feels human.
        </p>
      </section>

      {/* Divider */}
      <div className="max-w-6xl mx-auto px-6">
        <div className="h-px bg-gradient-to-r from-transparent via-[#d4c4a8]/15 to-transparent" />
      </div>

      {/* Who We Serve */}
      <section className="max-w-4xl mx-auto px-6 py-20">
        <div className="flex items-center justify-center gap-3 mb-8">
          <Users className="w-6 h-6 text-[#e89b47]" />
          <h2 className="font-display text-2xl sm:text-3xl text-[#e8dcc8]" data-testid="who-we-serve-heading">
            Who We Serve
          </h2>
        </div>
        <div className="max-w-2xl mx-auto text-center space-y-5 mb-8">
          <p className="font-body text-lg text-[#d4c4a8]/80 leading-relaxed">We work with anyone building something with heart:</p>
          <p className="font-body text-lg text-[#e8dcc8] leading-relaxed">
            {WHO_WE_SERVE.join(', ').replace(/, ([^,]*)$/, ', and $1')}.
          </p>
        </div>
        <p className="font-body text-[#d4c4a8]/70 text-center italic max-w-2xl mx-auto">
          If you care about the people behind the metrics, you&rsquo;re in the right place.
        </p>
      </section>

      {/* CTA */}
      <section className="max-w-3xl mx-auto px-6 pb-20 text-center">
        <a
          href="https://calendly.com/marketingtarot/30min"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 h-14 px-10 text-base bg-gradient-to-r from-[#e89b47] to-[#d4883a] text-[#0a1628] font-display tracking-widest rounded-xl shadow-lg shadow-[#e89b47]/25 transition-all hover:scale-105 hover:shadow-xl hover:shadow-[#e89b47]/30"
          data-testid="about-cta-btn"
        >
          Book a Clarity Call
          <ArrowRight className="w-5 h-5" />
        </a>
      </section>

      {/* Footer */}
      <SiteFooter />
    </div>
  );
};

export default AboutPage;
